import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import LogFormShell from '../../../components/UI/LogFormShell';
import LogDateField from '../../../components/UI/LogDateField';
import LogTimeField from '../../../components/UI/LogTimeField';
import LogDatePickerPopover from '../../../components/UI/LogDatePickerPopover';
import StyledButton from '../../../components/UI/StyledButton';
import { auth, db } from '../../../services/firebase';
import { fetchMedications } from '../../../services/medicationService';
import colors from '../../../assets/theme/colors';
import MedicationDuplicateWarningDialog from './MedicationDuplicateWarningDialog';
import {
  detectPossibleMedicationDuplicate,
  fetchMedicationLogsForChildDate,
  normalizeMedicationLogRecord,
} from '../../../services/medicationLogDuplicateService';
import {
  createMedicationLogEntry,
  getActiveDoseAtTime,
} from '../../../components/Dashboard/shared/childMedicationHelpers';

const getDefaultBackfillDate = () => new Date();

const formatSheetDateValue = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return 'Select date';
  }

  return dateValue.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatLocalDateInput = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return '';
  }

  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildLocalDateTime = (dateValue, timeValue) => {
  const next = new Date(dateValue);
  if (timeValue) {
    const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
    if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
      next.setHours(hours, minutes, 0, 0);
    }
  }
  return next;
};

const getCurrentTimeValue = (date = new Date()) => new Date(date).toTimeString().slice(0, 5);

const normalizeMedicationSchedules = (medication) => {
  if (!medication) {
    return [];
  }

  const schedules = Array.isArray(medication.schedules) && medication.schedules.length
    ? medication.schedules
    : [{
        id: `${medication.id || 'med'}-0`,
        dose: medication.dose || '',
        unit: medication.unit || 'mg',
        time: medication.time || '',
      }];

  return schedules.map((schedule, index) => ({
    id: schedule.id || `${medication.id || 'med'}-${index}`,
    dose: schedule.dose || medication.dose || '',
    unit: schedule.unit || medication.unit || 'mg',
    time: schedule.time || medication.time || '',
  }));
};

const MedicationBackfillDialog = ({
  open,
  childId,
  childName,
  medications = [],
  initialMedicationId = '',
  initialScheduleId = '',
  initialStatus = 'taken',
  initialTime = '',
  onClose,
  onSaved,
}) => {
  const [authUser] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [selectedMedicationId, setSelectedMedicationId] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [selectedDate, setSelectedDate] = useState(getDefaultBackfillDate());
  const [selectedTime, setSelectedTime] = useState(getCurrentTimeValue());
  const [selectedDose, setSelectedDose] = useState('');
  const [status, setStatus] = useState('taken');
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [showDateField, setShowDateField] = useState(false);
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  useEffect(() => {
    let active = true;

    const loadMedications = async () => {
      if (!open || !childId) {
        setMedicationOptions([]);
        return;
      }

      if (Array.isArray(medications) && medications.length > 0) {
        setMedicationOptions(medications.filter((item) => !item?.isArchived));
        return;
      }

      try {
        const fetched = await fetchMedications(childId, false);
        if (active) {
          setMedicationOptions((Array.isArray(fetched) ? fetched : []).filter((item) => !item?.isArchived));
        }
      } catch (error) {
        console.error('Error loading medications for backfill:', error);
        if (active) {
          setMedicationOptions([]);
        }
      }
    };

    loadMedications();
    return () => {
      active = false;
    };
  }, [open, childId, medications]);

  useEffect(() => {
    if (!open) {
      setDuplicatePrompt(null);
      return;
    }

    setSelectedDate(getDefaultBackfillDate());
    setSelectedTime(initialTime || getCurrentTimeValue());
    setStatus(initialStatus || 'taken');
    setSelectedMedicationId(initialMedicationId || '');
    setSelectedScheduleId(initialScheduleId || '');
    setSelectedDose('');
    setShowDateField(false);
  }, [open, childId, initialTime, initialStatus, initialMedicationId, initialScheduleId]);

  useEffect(() => {
    if (!medicationOptions.length) {
      setSelectedMedicationId('');
      setSelectedScheduleId('');
      return;
    }

    setSelectedMedicationId((current) => {
      if (initialMedicationId && medicationOptions.some((item) => item.id === initialMedicationId)) {
        return initialMedicationId;
      }
      if (current && medicationOptions.some((item) => item.id === current)) {
        return current;
      }
      return medicationOptions[0].id;
    });
  }, [medicationOptions, initialMedicationId]);

  const selectedMedication = useMemo(() => (
    medicationOptions.find((item) => item.id === selectedMedicationId) || null
  ), [medicationOptions, selectedMedicationId]);

  const selectedSchedule = useMemo(() => {
    const schedules = normalizeMedicationSchedules(selectedMedication);
    if (!schedules.length) {
      return null;
    }

    return schedules.find((schedule) => schedule.id === selectedScheduleId) || schedules[0];
  }, [selectedMedication, selectedScheduleId]);

  useEffect(() => {
    if (!selectedMedication) {
      setSelectedScheduleId('');
      setSelectedDose('');
      return;
    }

    const schedules = normalizeMedicationSchedules(selectedMedication);
    if (!schedules.length) {
      setSelectedScheduleId('');
      setSelectedDose('');
      return;
    }

    setSelectedScheduleId((current) => {
      if (initialScheduleId && schedules.some((schedule) => schedule.id === initialScheduleId)) {
        return initialScheduleId;
      }
      if (current && schedules.some((schedule) => schedule.id === current)) {
        return current;
      }
      return schedules[0].id;
    });
  }, [selectedMedication, initialScheduleId]);

  useEffect(() => {
    if (!selectedMedication) {
      setSelectedDose('');
      return;
    }

    const timestamp = buildLocalDateTime(selectedDate, selectedTime);
    const resolvedDose = getActiveDoseAtTime(selectedMedication, timestamp, medicationOptions);

    setSelectedScheduleId(resolvedDose?.schedule?.id || '');
    setSelectedDose(resolvedDose?.resolved ? (resolvedDose.plannedDose || selectedMedication?.dose || '') : '');
  }, [selectedMedication, selectedDate, selectedTime, medicationOptions]);

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (nextDate) => {
    if (!nextDate) {
      return;
    }

    setSelectedDate(nextDate);
    setDatePickerAnchor(null);
  };

  const buildBackfillDoc = (nextStatus = status) => {
    const timestamp = buildLocalDateTime(selectedDate, selectedTime);
    const medicationName = selectedMedication?.name || '';
    const scheduleTime = selectedSchedule?.time || selectedTime || '';
    const takenTimestamp = buildLocalDateTime(selectedDate, selectedTime);
    const scheduledTimestamp = buildLocalDateTime(selectedDate, scheduleTime);
    const localDate = formatLocalDateInput(selectedDate);
    const resolvedEntry = createMedicationLogEntry({
      childId,
      medication: selectedMedication,
      medications: medicationOptions,
      timestamp,
      actualTime: takenTimestamp,
      actualDose: nextStatus === 'taken' ? selectedDose : '',
      status: nextStatus === 'taken' ? 'taken' : 'missed',
      authorId: authUser?.uid || '',
      authorName: authUser?.displayName || authUser?.email?.split('@')[0] || 'User',
      authorEmail: authUser?.email || '',
    });
    const plannedDose = resolvedEntry.plannedDose || selectedSchedule?.dose || selectedMedication?.dose || '';
    const actualDose = nextStatus === 'taken'
      ? (selectedDose || plannedDose || selectedMedication?.dose || '')
      : '';

    return {
      ...resolvedEntry,
      status: nextStatus === 'taken' ? 'taken' : 'missed',
      category: 'medication',
      source: 'medication_backfill',
      text: `${nextStatus === 'taken' ? 'Backfilled taken' : 'Backfilled missed'} dose for ${medicationName}`.trim(),
      entryDate: localDate,
      timestamp: takenTimestamp,
      timestampUtc: takenTimestamp.toISOString(),
      localDate,
      localTime: selectedTime,
      medicationId: selectedMedication?.id || '',
      medicationName,
      medicationScheduleId: selectedSchedule?.id || resolvedEntry.medicationScheduleId || '',
      medicationScheduleIndex: 0,
      medicationScheduleKey: [selectedMedication?.id || '', selectedSchedule?.id || '0', localDate].join(':'),
      medicationScheduleTime: scheduleTime,
      medicationScheduleDose: plannedDose,
      medicationScheduleUnit: selectedSchedule?.unit || selectedMedication?.unit || 'mg',
      plannedDose,
      actualDose,
      dose: actualDose ?? plannedDose,
      medicationCategory: selectedMedication?.category || '',
      medicationFrequency: selectedMedication?.frequency || '',
      scheduledFor: scheduledTimestamp.toISOString(),
      takenAt: nextStatus === 'taken' ? takenTimestamp.toISOString() : null,
      backfill: true,
      backfillStatus: nextStatus,
      authorId: authUser?.uid,
      authorName: authUser?.displayName || authUser?.email?.split('@')[0] || 'User',
      authorEmail: authUser?.email,
      timeZoneName: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      timeZoneOffsetMinutes: -new Date().getTimezoneOffset(),
      createdBy: authUser?.uid,
      createdAt: serverTimestamp(),
    };
  };

  const handleSubmit = async ({ allowDuplicate = false, nextStatus = status } = {}) => {
    if (!childId || !authUser?.uid || !selectedMedication) {
      return;
    }

    setLoading(true);
    try {
      const docData = buildBackfillDoc(nextStatus);
      if (!allowDuplicate) {
        const existingLogs = await fetchMedicationLogsForChildDate(childId, selectedDate);
        const duplicateInfo = detectPossibleMedicationDuplicate(
          normalizeMedicationLogRecord({
            ...docData,
            childId,
            medicationId: selectedMedication?.id || '',
            medicationName: selectedMedication?.name || '',
            medicationScheduleId: selectedSchedule?.id || '',
            medicationScheduleIndex: 0,
            medicationScheduleTime: selectedSchedule?.time || selectedTime || '',
            medicationScheduleDose: selectedSchedule?.dose || selectedMedication?.dose || '',
            medicationScheduleUnit: selectedSchedule?.unit || selectedMedication?.unit || 'mg',
            medicationCategory: selectedMedication?.category || '',
            medicationFrequency: selectedMedication?.frequency || '',
          }),
          existingLogs
        );

        if (duplicateInfo.matched) {
          setDuplicatePrompt({
            reason: duplicateInfo.reason,
            medicationName: selectedMedication?.name || '',
            existingTimeLabel: duplicateInfo.existingLog?.timeLabel || '',
            onConfirm: () => {
              setDuplicatePrompt(null);
              handleSubmit({ allowDuplicate: true, nextStatus });
            },
          });
          return;
        }
      }

      const docRef = await addDoc(collection(db, 'dailyLogs'), docData);
      window.dispatchEvent(new CustomEvent('captureez:timeline-entry-created', {
        detail: {
          id: docRef.id,
          collection: 'dailyLogs',
          ...docData,
          status: nextStatus,
        },
      }));

      onSaved?.({
        medicationName: selectedMedication?.name || '',
        status: nextStatus,
        scheduledFor: docData.scheduledFor,
        takenAt: docData.takenAt,
      });

      onClose?.();
    } catch (error) {
      console.error('Error saving medication backfill:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTakenClick = () => {
    setStatus('taken');
    handleSubmit({ nextStatus: 'taken' });
  };

  const handleMissedClick = () => {
    setStatus('missed');
  };

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title="Log medication"
      subtitle="Tap to mark as taken"
      titleBadge={childName || undefined}
      compactTitle
      mobileBreakpoint="md"
      bodySx={{
        pt: 1.25,
        pb: 1.5,
        px: { xs: 1.1, sm: 2 },
      }}
      surfaceSx={{
        height: 'min(90vh, 90vh)',
        maxHeight: 'min(90vh, 90vh)',
      }}
    >
      <Stack spacing={1.25}>
        <TextField
          select
          fullWidth
          label="Medication"
          value={selectedMedicationId}
          onChange={(event) => setSelectedMedicationId(event.target.value)}
        >
          {medicationOptions.length > 0 ? (
            medicationOptions.map((medication) => (
              <MenuItem key={medication.id} value={medication.id}>
                {medication.name}
              </MenuItem>
            ))
          ) : (
            <MenuItem value="">
              No active medications
            </MenuItem>
          )}
        </TextField>

        <TextField
          fullWidth
          label="Dose"
          value={selectedDose}
          onChange={(event) => setSelectedDose(event.target.value)}
          placeholder={selectedSchedule?.dose || selectedMedication?.dose || 'Dose'}
          InputProps={{
            endAdornment: (
              <Box component="span" sx={{ color: '#6b7280', fontSize: '0.95rem', mr: 0.25 }}>
                {selectedSchedule?.unit || selectedMedication?.unit || 'mg'}
              </Box>
            ),
          }}
        />
        {!selectedDose ? (
          <Box sx={{ color: '#6b7280', fontSize: '0.9rem', mt: -0.5 }}>
            No active dose found for this date/time. You can still save a manual entry.
          </Box>
        ) : null}

        <LogTimeField
          value={selectedTime}
          onChange={(event) => setSelectedTime(event.target.value)}
          label="Time"
          sx={{
            '& .MuiInputBase-root': {
              minHeight: 58,
              borderRadius: '18px',
            },
            '& input': {
              paddingTop: '0.8rem',
              paddingBottom: '0.75rem',
            },
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1,
          }}
        >
          <Button
            variant={status === 'taken' ? 'contained' : 'outlined'}
            onClick={handleTakenClick}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.15,
              bgcolor: status === 'taken' ? colors.brand.ink : 'transparent',
              color: status === 'taken' ? '#fff' : colors.brand.navy,
              borderColor: colors.landing.borderLight,
              '&:hover': {
                bgcolor: status === 'taken' ? colors.brand.deep : 'rgba(244, 241, 248, 0.6)',
              },
            }}
          >
            Taken
          </Button>
          <Button
            variant={status === 'missed' ? 'contained' : 'outlined'}
            onClick={handleMissedClick}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.15,
              bgcolor: status === 'missed' ? '#b91c1c' : 'transparent',
              color: status === 'missed' ? '#fff' : colors.brand.navy,
              borderColor: colors.landing.borderLight,
              '&:hover': {
                bgcolor: status === 'missed' ? '#991b1b' : 'rgba(244, 241, 248, 0.6)',
              },
            }}
          >
            Missed
          </Button>
        </Box>

        <Button
          type="button"
          variant="text"
          onClick={() => setShowDateField((current) => !current)}
          sx={{
            alignSelf: 'flex-start',
            minWidth: 0,
            px: 0,
            py: 0,
            textTransform: 'none',
            fontWeight: 650,
            color: '#6b7280',
            whiteSpace: 'nowrap',
            '&:hover': {
              bgcolor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {showDateField ? 'Hide date' : 'Different date'}
        </Button>

        {showDateField ? (
          <LogDateField
            label="Date"
            value={formatSheetDateValue(selectedDate)}
            onClick={handleDatePickerOpen}
          />
        ) : null}

        <StyledButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !selectedMedication}
          sx={{
            minWidth: 180,
            height: 58,
            borderRadius: '24px',
            bgcolor: colors.brand.ink,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            width: '100%',
            '&:hover': { bgcolor: colors.brand.deep },
          }}
      >
        {loading ? 'Saving...' : 'Save entry'}
        </StyledButton>
      </Stack>

      <MedicationDuplicateWarningDialog
        open={Boolean(duplicatePrompt)}
        reason={duplicatePrompt?.reason || ''}
        medicationName={duplicatePrompt?.medicationName || ''}
        existingTimeLabel={duplicatePrompt?.existingTimeLabel || ''}
        onKeepExisting={() => setDuplicatePrompt(null)}
        onLogAgainAnyway={() => duplicatePrompt?.onConfirm?.()}
      />

      <LogDatePickerPopover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleDatePickerClose}
        value={selectedDate}
        onChange={handleDateChange}
      />
    </LogFormShell>
  );
};

export default MedicationBackfillDialog;
